import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Archive,
  Bot,
  Calendar,
  ChevronLeft,
  Edit3,
  Eye,
  Megaphone,
  MousePointerClick,
  Plus,
  RefreshCw,
  Save,
  Sparkles,
  Users,
  X,
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';
const SURFACE = 'direct_support_floating_chat';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'customer', label: 'Tiendas' },
  { value: 'driver', label: 'Motorizados' },
  { value: 'provider', label: 'Proveedores' },
];

const EMPTY_FORM = {
  title: '',
  body: '',
  category: 'education',
  audience_roles: ['customer'],
  surface: SURFACE,
  status: 'draft',
  priority: 5,
  weight: 3,
  is_pinned: false,
  starts_at: '',
  ends_at: '',
  max_impressions_per_user: 4,
  cooldown_hours: 12,
  cta_label: 'Abrir chat',
  cta_action: 'open_chat',
  cta_payload_raw: '{}',
};

function authConfig(extra = {}) {
  const token = window.localStorage.getItem('token');
  return {
    ...extra,
    headers: {
      ...(extra.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Intl.DateTimeFormat('es-PE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (_) {
    return '-';
  }
}

function toDateTimeLocal(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    const offset = date.getTimezoneOffset();
    const local = new Date(date.getTime() - offset * 60 * 1000);
    return local.toISOString().slice(0, 16);
  } catch (_) {
    return '';
  }
}

function statusClasses(status) {
  if (status === 'active') return 'bg-emerald-100 text-emerald-700';
  if (status === 'archived') return 'bg-slate-100 text-slate-600';
  return 'bg-amber-100 text-amber-700';
}

function compactError(err, fallback) {
  const payload = err?.response?.data;
  return payload?.message || payload?.error || err?.message || fallback;
}

function normalizeTipToForm(tip) {
  if (!tip) return EMPTY_FORM;
  return {
    title: tip.title || '',
    body: tip.body || '',
    category: tip.category || 'education',
    audience_roles: tip.audience_roles?.length ? tip.audience_roles : ['customer'],
    surface: tip.surface || SURFACE,
    status: tip.status || 'draft',
    priority: tip.priority ?? 5,
    weight: tip.weight ?? 3,
    is_pinned: Boolean(tip.is_pinned),
    starts_at: toDateTimeLocal(tip.starts_at),
    ends_at: toDateTimeLocal(tip.ends_at),
    max_impressions_per_user: tip.max_impressions_per_user ?? 4,
    cooldown_hours: tip.cooldown_hours ?? 12,
    cta_label: tip.cta_label || '',
    cta_action: tip.cta_action || 'open_chat',
    cta_payload_raw: JSON.stringify(tip.cta_payload || {}, null, 2),
  };
}

function TipModal({ tip, onClose, onSaved }) {
  const [form, setForm] = useState(() => normalizeTipToForm(tip));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const isEditing = Boolean(tip?.id);

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  function toggleRole(role) {
    setForm(prev => {
      const exists = prev.audience_roles.includes(role);
      const nextRoles = exists
        ? prev.audience_roles.filter(item => item !== role)
        : [...prev.audience_roles, role];
      return { ...prev, audience_roles: nextRoles.length ? nextRoles : ['customer'] };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let ctaPayload = {};
      try {
        ctaPayload = form.cta_payload_raw?.trim() ? JSON.parse(form.cta_payload_raw) : {};
      } catch (_) {
        throw new Error('CTA payload debe ser JSON valido.');
      }

      const payload = {
        ...form,
        priority: Number(form.priority),
        weight: Number(form.weight),
        max_impressions_per_user: Number(form.max_impressions_per_user),
        cooldown_hours: Number(form.cooldown_hours),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        cta_payload: ctaPayload,
      };
      delete payload.cta_payload_raw;

      const response = isEditing
        ? await axios.patch(`${API}/api/admin/assistant-tips/${tip.id}`, payload, authConfig())
        : await axios.post(`${API}/api/admin/assistant-tips`, payload, authConfig());
      onSaved(response.data.tip);
    } catch (err) {
      setError(compactError(err, 'No se pudo guardar el tip.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[5200] flex items-center justify-center p-4">
      <button className="absolute inset-0 bg-slate-950/50" onClick={onClose} aria-label="Cerrar" />
      <form onSubmit={handleSubmit} className="relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-black text-slate-950">
              {isEditing ? 'Editar assistant tip' : 'Crear assistant tip'}
            </h2>
            <p className="text-xs font-semibold text-slate-500">
              Controla anuncios, educacion y descubrimiento de funcionalidades.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 sm:col-span-2">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Titulo</span>
              <input
                value={form.title}
                onChange={event => updateField('title', event.target.value)}
                maxLength={140}
                required
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
              />
            </label>

            <label className="space-y-1 sm:col-span-2">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Mensaje</span>
              <textarea
                value={form.body}
                onChange={event => updateField('body', event.target.value)}
                required
                rows={4}
                className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
              />
            </label>

            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Categoria</span>
              <input
                value={form.category}
                onChange={event => updateField('category', event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
              />
            </label>

            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Estado</span>
              <select
                value={form.status}
                onChange={event => updateField('status', event.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#B1336E]"
              >
                <option value="draft">Borrador</option>
                <option value="active">Activo</option>
                <option value="archived">Archivado</option>
              </select>
            </label>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-wide text-slate-500">
              <Users className="h-4 w-4" /> Audiencia
            </div>
            <div className="flex flex-wrap gap-2">
              {ROLE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => toggleRole(option.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-black ${
                    form.audience_roles.includes(option.value)
                      ? 'bg-[#B1336E] text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-4">
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Prioridad</span>
              <input type="number" min="0" max="100" value={form.priority} onChange={event => updateField('priority', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Peso random</span>
              <input type="number" min="1" max="100" value={form.weight} onChange={event => updateField('weight', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Max vistas</span>
              <input type="number" min="1" max="100" value={form.max_impressions_per_user} onChange={event => updateField('max_impressions_per_user', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Cooldown h</span>
              <input type="number" min="0" max="720" value={form.cooldown_hours} onChange={event => updateField('cooldown_hours', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
          </div>

          <label className="flex items-center gap-2 rounded-xl border border-purple-100 bg-purple-50 p-3 text-sm font-black text-purple-800">
            <input
              type="checkbox"
              checked={form.is_pinned}
              onChange={event => updateField('is_pinned', event.target.checked)}
              className="h-4 w-4"
            />
            Pinned / lanzamiento importante
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Inicio</span>
              <input type="datetime-local" value={form.starts_at} onChange={event => updateField('starts_at', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">Fin</span>
              <input type="datetime-local" value={form.ends_at} onChange={event => updateField('ends_at', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">CTA label</span>
              <input value={form.cta_label} onChange={event => updateField('cta_label', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold" />
            </label>
            <label className="space-y-1">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">CTA action</span>
              <select value={form.cta_action} onChange={event => updateField('cta_action', event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold">
                <option value="open_chat">Abrir chat</option>
                <option value="url_hash">Cambiar hash URL</option>
                <option value="none">Sin accion</option>
              </select>
            </label>
            <label className="space-y-1 sm:col-span-2">
              <span className="text-xs font-black uppercase tracking-wide text-slate-500">CTA payload JSON</span>
              <textarea value={form.cta_payload_raw} onChange={event => updateField('cta_payload_raw', event.target.value)} rows={3} className="w-full resize-none rounded-xl border border-slate-200 px-3 py-2 font-mono text-xs outline-none focus:border-[#B1336E]" />
            </label>
          </div>

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-black text-slate-600 hover:bg-slate-200">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-black text-white hover:bg-[#9d2d62] disabled:opacity-50">
            <Save className="h-4 w-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminAssistantTipsManager({ setActiveComponent }) {
  const [tips, setTips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalTip, setModalTip] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const [tipsResponse, summaryResponse] = await Promise.all([
        axios.get(`${API}/api/admin/assistant-tips`, authConfig({ params })),
        axios.get(`${API}/api/admin/assistant-tips/summary`, authConfig()),
      ]);
      setTips(tipsResponse.data.tips || []);
      setSummary(summaryResponse.data.summary || null);
    } catch (err) {
      setError(compactError(err, 'No se pudieron cargar los assistant tips.'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTips = useMemo(() => tips, [tips]);

  function openCreate() {
    setModalTip(null);
    setModalOpen(true);
  }

  function openEdit(tip) {
    setModalTip(tip);
    setModalOpen(true);
  }

  async function archiveTip(tip) {
    if (!window.confirm(`Archivar "${tip.title}"?`)) return;
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${API}/api/admin/assistant-tips/${tip.id}`, authConfig());
      await loadData();
    } catch (err) {
      setError(compactError(err, 'No se pudo archivar el tip.'));
      setLoading(false);
    }
  }

  function handleSaved() {
    setModalOpen(false);
    setModalTip(null);
    loadData();
  }

  const statCards = [
    { label: 'Activos', value: summary?.active || 0, icon: Sparkles, color: 'text-emerald-700 bg-emerald-50' },
    { label: 'Pinned', value: summary?.pinned || 0, icon: Megaphone, color: 'text-purple-700 bg-purple-50' },
    { label: 'Vistas', value: summary?.shown || 0, icon: Eye, color: 'text-blue-700 bg-blue-50' },
    { label: 'Clicks', value: summary?.clicked || 0, icon: MousePointerClick, color: 'text-[#B1336E] bg-[#B1336E]/10' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setActiveComponent('OptionsAdmin')} className="rounded-xl bg-slate-100 p-2 text-slate-600 hover:bg-slate-200">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#B1336E]/10 text-[#B1336E]">
              <Bot className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-950">Assistant Tips</h1>
              <p className="text-xs font-semibold text-slate-500">Mensajes educativos y anuncios para el boton flotante.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={loadData} disabled={loading} className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-black text-slate-600 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refrescar
            </button>
            <button onClick={openCreate} className="inline-flex items-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-black text-white shadow-sm hover:bg-[#9d2d62]">
              <Plus className="h-4 w-4" />
              Nuevo tip
            </button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl space-y-5 px-4 py-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {statCards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wide text-slate-400">{card.label}</p>
                    <p className="mt-1 text-2xl font-black text-slate-950">{card.value}</p>
                  </div>
                  <div className={`rounded-xl p-3 ${card.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-black text-slate-950">Tips configurados</h2>
              <p className="text-xs font-semibold text-slate-500">Los tips activos son elegidos por prioridad, pinned y random ponderado.</p>
            </div>
            <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-black text-slate-700">
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="draft">Borradores</option>
              <option value="archived">Archivados</option>
            </select>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          )}

          {loading && !filteredTips.length ? (
            <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 p-10 text-sm font-semibold text-slate-500">
              <RefreshCw className="h-4 w-4 animate-spin" /> Cargando tips...
            </div>
          ) : !filteredTips.length ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-sm font-semibold text-slate-500">
              No hay tips para este filtro.
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3">Tip</th>
                      <th className="px-4 py-3">Audiencia</th>
                      <th className="px-4 py-3">Estado</th>
                      <th className="px-4 py-3">Peso</th>
                      <th className="px-4 py-3">Ventana</th>
                      <th className="px-4 py-3">Stats</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {filteredTips.map(tip => (
                      <tr key={tip.id} className="hover:bg-slate-50">
                        <td className="max-w-md px-4 py-3">
                          <div className="flex items-start gap-2">
                            {tip.is_pinned && <Megaphone className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />}
                            <div className="min-w-0">
                              <div className="font-black text-slate-950">{tip.title}</div>
                              <div className="line-clamp-2 text-xs font-semibold text-slate-500">{tip.body}</div>
                              <div className="mt-1 text-[10px] font-black uppercase text-[#B1336E]">{tip.category}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(tip.audience_roles || []).map(role => (
                              <span key={role} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-600">{role}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`rounded-full px-2 py-1 text-[11px] font-black ${statusClasses(tip.status)}`}>
                            {tip.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-black text-slate-600">
                          P{tip.priority} / W{tip.weight}
                        </td>
                        <td className="px-4 py-3 text-xs font-semibold text-slate-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(tip.starts_at)}
                          </div>
                          <div className="mt-1">{formatDate(tip.ends_at)}</div>
                        </td>
                        <td className="px-4 py-3 text-xs font-black text-slate-600">
                          <div>{tip.stats?.shown || 0} vistas</div>
                          <div>{tip.stats?.clicked || 0} clicks</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button onClick={() => openEdit(tip)} className="rounded-lg bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" title="Editar">
                              <Edit3 className="h-4 w-4" />
                            </button>
                            {tip.status !== 'archived' && (
                              <button onClick={() => archiveTip(tip)} className="rounded-lg bg-red-50 p-2 text-red-600 hover:bg-red-100" title="Archivar">
                                <Archive className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>

      {modalOpen && (
        <TipModal
          tip={modalTip}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
